import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Icon } from "./Icon";
import {
  ATTACHMENT,
  BUDGET_RANGES,
  COMPANY,
  INQUIRY_TYPES,
  PARTNER,
  PARTNER_TYPE,
  PROJECT_STAGES,
} from "./data";
import { BOTTOM_CTA, CONTRACT_MODELS, CONTRACT_ROWS, STAGES } from "./content";
import { SEGMENTS, SEGMENT_OTHER, segmentLink } from "./segments";

/* 개발 문의에서 공동개발(Partnership) 문의를 골라 둔 주소 */
export const PARTNER_LINK = `/contact?type=${encodeURIComponent(PARTNER_TYPE)}`;

/* --------------------------------------------------------
   페이지별 title / description / canonical / og — 런타임에서 설정
   빌드 뒤 scripts/prerender.js 가 이 값이 들어간 HTML 을 페이지마다 저장하므로
   검색로봇 · 링크 미리보기도 페이지별 값을 읽는다.
   og · twitter 는 따로 주지 않으면 title · description 을 그대로 쓴다.
   noindex — 장바구니 · 주문 · 404 처럼 검색 결과에 나올 필요가 없는 페이지
   -------------------------------------------------------- */
function setHeadTag(selector, create, attr, value) {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement(create.tag);
    Object.entries(create.attrs).forEach(([k, v]) => el.setAttribute(k, v));
    document.head.appendChild(el);
  }
  el.setAttribute(attr, value);
}

const metaName = (name, value) =>
  setHeadTag(`meta[name="${name}"]`, { tag: "meta", attrs: { name } }, "content", value);
const metaProp = (property, value) =>
  setHeadTag(`meta[property="${property}"]`, { tag: "meta", attrs: { property } }, "content", value);

export function useSeo({ title, description, path, ogTitle, ogDescription, noindex }) {
  useEffect(() => {
    if (title) document.title = title;
    if (description) metaName("description", description);

    const shareTitle = ogTitle || title;
    const shareDesc = ogDescription || description;
    if (shareTitle) {
      metaProp("og:title", shareTitle);
      metaName("twitter:title", shareTitle);
    }
    if (shareDesc) {
      metaProp("og:description", shareDesc);
      metaName("twitter:description", shareDesc);
    }

    if (path) {
      setHeadTag('link[rel="canonical"]', { tag: "link", attrs: { rel: "canonical" } }, "href", COMPANY.origin + path);
      metaProp("og:url", COMPANY.origin + path);
    }

    const robots = document.head.querySelector('meta[name="robots"]');
    if (noindex) metaName("robots", "noindex");
    else if (robots) robots.remove();
  }, [title, description, path, ogTitle, ogDescription, noindex]);
}

/* --------------------------------------------------------
   공통 조각
   -------------------------------------------------------- */
export function SectionHead({ id, kicker, title, desc, action, center }) {
  return (
    <header className={`section__head ${center ? "section__head--center" : ""}`}>
      <div className="section__head-main">
        {kicker && <div className="kicker">{kicker}</div>}
        <h2 id={id} className="section__title">{title}</h2>
      </div>
      {(desc || action) && (
        <div className="section__head-side">
          {desc && <p className="section__desc">{desc}</p>}
          {action}
        </div>
      )}
    </header>
  );
}

/* 하위 페이지 상단 — 홈의 풀스크린 Hero 대신 쓰는 압축형 */
export function PageHero({ kicker, title, desc, children }) {
  return (
    <section className="page-hero">
      <div className="page-hero__glow" aria-hidden="true" />
      <div className="page-hero__inner">
        {kicker && <div className="kicker">{kicker}</div>}
        <h1 className="page-hero__title">{title}</h1>
        {desc && <p className="page-hero__lead">{desc}</p>}
        {children}
      </div>
    </section>
  );
}

export function CardGrid({ items, cols }) {
  return (
    <div className={`grid grid--cards ${cols === 2 ? "grid--2" : ""}`}>
      {items.map((it) => {
        const I = Icon[it.icon];
        return (
          <article key={it.title} className="card">
            <div className="card__icon">
              {I ? <I width="22" height="22" aria-hidden="true" focusable="false" /> : null}
            </div>
            <h3 className="card__title">{it.title}</h3>
            <p className="card__desc">{it.desc}</p>
            {it.points && (
              <ul className="card__points">
                {it.points.map((p) => (
                  <li key={p}>
                    <Icon.Check width="14" height="14" aria-hidden="true" focusable="false" />
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
            )}
          </article>
        );
      })}
    </div>
  );
}

/* 페이지 하단 전환 유도 배너 — 기본은 문서 §5.1-9 하단 CTA, 목적지 개발 문의(공동개발 문의를 골라 둔다) */
export function CtaBand({
  title = BOTTOM_CTA,
  desc = "",
  to = PARTNER_LINK,
  label = "개발 문의 하기",
}) {
  return (
    <section className="section">
      <div className="cta-band">
        <div>
          <h2 className="cta-band__title">{title}</h2>
          {desc && <p className="cta-band__desc">{desc}</p>}
        </div>
        <div className="cta-band__actions">
          <Link to={to} className="btn btn--primary">
            {label} <Icon.ArrowRight width="16" height="16" aria-hidden="true" focusable="false" />
          </Link>
          <a href={`tel:${COMPANY.phone.replace(/-/g, "")}`} className="btn btn--ghost">
            {COMPANY.phone}
          </a>
        </div>
      </div>
    </section>
  );
}

/* 핵심 · 보조 메시지를 크게 보여 주는 한 줄 띠 */
export function MessageBand({ text, sub }) {
  return (
    <section className="section section--msg">
      <p className="msg-band">{text}</p>
      {sub && <p className="msg-band__sub">{sub}</p>}
    </section>
  );
}

/* 접고 펼치는 FAQ 목록 */
export function FaqList({ items }) {
  return (
    <div className="faq">
      {items.map((f) => (
        <details key={f.q} className="faq__item">
          <summary className="faq__q">
            <span>{f.q}</span>
            <span className="faq__mark" aria-hidden="true" />
          </summary>
          <p className="faq__a">{f.a}</p>
        </details>
      ))}
    </div>
  );
}

/* "이런 분들과 만듭니다" — 아이콘 + 세그먼트명 + 니즈 예시 + [개발 문의 하기]
   featured 만 먼저 보이고 나머지는 '더 보기' */
export function SegmentCards({ showAll = false }) {
  const [expanded, setExpanded] = useState(showAll);
  const featured = SEGMENTS.filter((s) => s.featured);
  const rest = SEGMENTS.filter((s) => !s.featured);
  const list = expanded ? [...featured, ...rest] : featured;

  return (
    <>
      <div className="seg-grid">
        {list.map((s) => {
          const I = Icon[s.icon];
          return (
            <article key={s.id} className="seg">
              <div className="seg__head">
                <div className="card__icon">
                  {I ? <I width="20" height="20" aria-hidden="true" focusable="false" /> : null}
                </div>
                <div>
                  <h3 className="seg__title">{s.label}</h3>
                  <p className="seg__who">{s.who}</p>
                </div>
              </div>
              <ul className="seg__needs">
                {s.needs.map((n) => (
                  <li key={n}>
                    <Icon.Check width="14" height="14" aria-hidden="true" focusable="false" />
                    <span>{n}</span>
                  </li>
                ))}
              </ul>
              <Link to={segmentLink(s)} className="seg__link">
                개발 문의 하기 <Icon.ArrowRight width="13" height="13" aria-hidden="true" focusable="false" />
              </Link>
            </article>
          );
        })}
      </div>
      {!expanded && rest.length > 0 && (
        <div className="seg-more">
          <button type="button" className="btn btn--ghost" onClick={() => setExpanded(true)}>
            더 보기 ({rest.length})
          </button>
        </div>
      )}
    </>
  );
}

/* 한 줄 플로우 다이어그램 — 간결한 라인 스타일 (문서 §6) */
export function FlowLine({ items, numbered = false }) {
  return (
    <ol className={`flowline ${numbered ? "flowline--numbered" : ""}`} style={{ "--n": items.length }}>
      {items.map((it, i) => (
        <li key={it} className="flowline__item">
          <span className="flowline__dot" aria-hidden="true">{numbered ? i + 1 : ""}</span>
          <span className="flowline__label">{it}</span>
        </li>
      ))}
    </ol>
  );
}

/* 3가지 계약 방식 — compact 면 홈 요약본(고객 부담 · 기술투자 · 보상 3줄) */
export function ContractTable({ compact = false }) {
  const rows = compact ? CONTRACT_ROWS.slice(0, 3) : CONTRACT_ROWS;

  return (
    <>
      {/* 좁은 화면 · 홈 요약 — 방식별 카드 */}
      <div className={`model-cards ${compact ? "" : "model-cards--mobile"}`}>
        {CONTRACT_MODELS.map((m) => (
          <article key={m.id} className={`model-card ${m.highlight ? "model-card--accent" : ""}`}>
            <h3 className="model-card__name">{m.name}</h3>
            <dl className="model-card__rows">
              {rows.map((r) => (
                <div key={r.key}><dt>{r.label}</dt><dd>{m.cells[r.key]}</dd></div>
              ))}
            </dl>
          </article>
        ))}
      </div>

      {!compact && (
        <div className="cmp">
          <table className="cmp__table">
            <thead>
              <tr>
                <th scope="col">구분</th>
                {CONTRACT_MODELS.map((m) => (
                  <th key={m.id} scope="col" className={m.highlight ? "is-accent" : ""}>{m.name}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {CONTRACT_ROWS.map((r) => (
                <tr key={r.key}>
                  <th scope="row">{r.label}</th>
                  {CONTRACT_MODELS.map((m) => (
                    <td key={m.id} className={m.highlight ? "is-accent" : ""}>{m.cells[r.key]}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

/* 진행 단계 — 단계 · 내용 · 기간 · 비용 */
export function StageStrip() {
  return (
    <ol className="stage-strip">
      {STAGES.map((s) => (
        <li key={s.step} className="stage-strip__item">
          <div className="stage-strip__no">{s.step}</div>
          <h3 className="stage-strip__title">{s.title}</h3>
          <p className="stage-strip__desc">{s.desc}</p>
          <div className="stage-strip__meta">
            <span>{s.period}</span>
            <span>{s.cost}</span>
          </div>
        </li>
      ))}
    </ol>
  );
}

/* --------------------------------------------------------
   개발 문의 폼 — /contact
   일반 개발 의뢰와 공동개발(Partnership) 문의를 함께 받는다. 문의 유형에서 PARTNER_TYPE 을 고르면
   심사 항목(사업자 형태 · 업종 · 판로 · 정부지원사업)이 열린다.
   필드 이름은 백엔드 app/inquiries.py 의 Form 파라미터와 일치해야 한다.
   -------------------------------------------------------- */
const EMPTY_FORM = {
  company: "",
  name: "",
  email: "",
  phone: "",
  inquiry_type: "",
  stage: "",
  schedule: "",
  budget: "",
  message: "",
  // 공동개발 문의 심사 항목
  biz_type: "",
  segment: "",        // 업종 선택 (세그먼트 label 또는 SEGMENT_OTHER) — 보낼 때 industry 로 바꾼다
  industry_other: "",
  sales_channel: "",
  gov_support: "",
  gov_program: "",
  gov_budget: "",
  gov_end_date: "",
  website: "", // 봇 트랩
};

const PARTNER_FIELDS = ["biz_type", "sales_channel", "gov_support", "gov_program", "gov_budget", "gov_end_date"];

function Choice({ name, options, value, onChange, disabled }) {
  return (
    <div className="choice" role="radiogroup">
      {options.map((o) => (
        <label key={o} className={`choice__item ${value === o ? "is-on" : ""}`}>
          <input
            type="radio" name={name} value={o} checked={value === o}
            onChange={onChange} disabled={disabled}
          />
          <span>{o}</span>
        </label>
      ))}
    </div>
  );
}

function Req() {
  return <span className="form__req" aria-hidden="true">*</span>;
}

function formatSize(bytes) {
  if (!Number.isFinite(bytes) || bytes <= 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  const units = ["KB", "MB"];
  let v = bytes / 1024;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i += 1;
  }
  return `${v >= 10 || i === 0 ? Math.round(v) : v.toFixed(1)} ${units[i]}`;
}

export function ContactForm({ defaultType, defaultSegment }) {
  const initial = { ...EMPTY_FORM, inquiry_type: defaultType || "", segment: defaultSegment || "" };
  const [form, setForm] = useState(initial);
  const [file, setFile] = useState(null);
  const [agree, setAgree] = useState(false);
  const [status, setStatus] = useState("idle"); // idle | sending | sent | error
  const [error, setError] = useState("");
  const [ticket, setTicket] = useState(null);
  const [sentPartner, setSentPartner] = useState(false);
  const [progress, setProgress] = useState(0);
  const fileRef = useRef(null);

  const update = (field) => (e) =>
    setForm((f) => ({ ...f, [field]: e.target.value }));

  function onPickFile(e) {
    const f = e.target.files && e.target.files[0];
    if (!f) {
      setFile(null);
      return;
    }
    if (f.size > ATTACHMENT.maxBytes) {
      setError(`첨부 파일은 ${ATTACHMENT.maxLabel} 까지 올릴 수 있습니다.`);
      setStatus("error");
      e.target.value = "";
      setFile(null);
      return;
    }
    setError("");
    if (status === "error") setStatus("idle");
    setFile(f);
  }

  function clearFile() {
    setFile(null);
    if (fileRef.current) fileRef.current.value = "";
  }

  const isPartner = form.inquiry_type === PARTNER_TYPE;
  const isOther = form.segment === SEGMENT_OTHER;
  const isFunded = form.gov_support === "선정됨";
  const industry = isOther ? form.industry_other.trim() : form.segment;

  /* 서버에서도 같은 검증을 하지만, 공동개발 문의는 항목이 많아 먼저 알려 주는 편이 덜 번거롭다 */
  function check() {
    if (isPartner) {
      const need = [
        [form.phone.trim(), "연락처"],
        [form.biz_type, "사업자 형태"],
        [industry, "현재 하는 일·업종"],
        [form.sales_channel, "판매 방법·판로"],
        [form.gov_support, "정부지원사업"],
      ];
      if (isFunded) {
        need.push([form.gov_program.trim(), "지원사업명"]);
        need.push([form.gov_budget.trim(), "개발 가능 예산"]);
        need.push([form.gov_end_date, "협약 종료일"]);
      }
      const missing = need.find(([v]) => !v);
      if (missing) return `공동개발 문의는 '${missing[1]}' 항목이 필요합니다.`;
    }
    if (!agree) return "개인정보 수집·이용에 동의하셔야 접수할 수 있습니다.";
    return "";
  }

  async function onSubmit(e) {
    e.preventDefault();
    if (status === "sending") return;

    const msg = check();
    if (msg) {
      setError(msg);
      setStatus("error");
      return;
    }

    setStatus("sending");
    setError("");
    setProgress(0);

    const body = new FormData();
    const { segment, industry_other, ...rest } = form;
    Object.entries(rest).forEach(([k, v]) => {
      if (isPartner || !PARTNER_FIELDS.includes(k)) body.append(k, v);
    });
    if (isPartner) body.append("industry", industry);
    body.append("privacy_agree", "true");
    if (file) body.append("attachment", file);

    try {
      // 첨부가 있으면 올라가는 정도를 보여줘야 한다. fetch 로는 업로드 진행률을
      // 알 수 없어 XHR 을 쓴다. Content-Type 은 브라우저가 boundary 와 함께 넣는다.
      const data = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open("POST", "/api/contact");

        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) {
            setProgress(Math.round((e.loaded / e.total) * 100));
          }
        };

        xhr.onload = () => {
          let parsed = {};
          try { parsed = JSON.parse(xhr.responseText || "{}"); } catch { parsed = {}; }
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve(parsed);
          } else {
            reject(new Error(
              typeof parsed.detail === "string"
                ? parsed.detail
                : "전송에 실패했습니다. 잠시 후 다시 시도해주세요."
            ));
          }
        };
        xhr.onerror = () => reject(new Error("네트워크 연결을 확인해주세요."));
        xhr.ontimeout = () => reject(new Error("전송 시간이 초과되었습니다. 다시 시도해주세요."));

        xhr.send(body);
      });

      setTicket(data.id || null);
      setSentPartner(isPartner);
      setForm(initial);
      clearFile();
      setAgree(false);
      setProgress(0);
      setStatus("sent");
    } catch (err) {
      setProgress(0);
      setError(err.message || "전송에 실패했습니다.");
      setStatus("error");
    }
  }

  if (status === "sent") {
    return (
      <div className="contact__card">
        <div className="form__done" role="status">
          <h3 className="form__done-title">개발 문의가 접수되었습니다.</h3>
          <p className="form__done-desc">
            {ticket ? `접수번호 #${ticket}. ` : ""}
            {sentPartner ? PARTNER.done : "영업일 기준 1일 안에 회신드리겠습니다."} 급하시면 아래로 바로 연락 주셔도 됩니다.
          </p>
          <div className="contact__row">
            <span className="contact__label">
              <Icon.Mail width="18" height="18" aria-hidden="true" focusable="false" /> 이메일
            </span>
            <a className="contact__value" href={`mailto:${COMPANY.email}`}>{COMPANY.email}</a>
          </div>
          <div className="contact__row">
            <span className="contact__label">
              <Icon.Phone width="18" height="18" aria-hidden="true" focusable="false" /> 전화
            </span>
            <a className="contact__value" href={`tel:${COMPANY.phone.replace(/-/g, "")}`}>{COMPANY.phone}</a>
          </div>
          <button
            type="button"
            className="btn btn--ghost btn--block"
            onClick={() => { setTicket(null); setStatus("idle"); }}
          >
            다시 문의하기
          </button>
        </div>
      </div>
    );
  }

  const sending = status === "sending";

  return (
    <form className="contact__card form" onSubmit={onSubmit} noValidate>
      <div className="form__row">
        <div className="form__field">
          <label className="form__label" htmlFor="cf-company">
            상호 · 기관명 <span className="form__opt">(선택)</span>
          </label>
          <input
            id="cf-company" className="form__input" type="text" maxLength={200}
            autoComplete="organization" value={form.company} onChange={update("company")} disabled={sending}
          />
        </div>

        <div className="form__field">
          <label className="form__label" htmlFor="cf-name">
            이름 <span className="form__req" aria-hidden="true">*</span>
          </label>
          <input
            id="cf-name" className="form__input" type="text" required maxLength={60}
            autoComplete="name" value={form.name} onChange={update("name")} disabled={sending}
          />
        </div>
      </div>

      <div className="form__row">
        <div className="form__field">
          <label className="form__label" htmlFor="cf-email">
            이메일 <span className="form__req" aria-hidden="true">*</span>
          </label>
          <input
            id="cf-email" className="form__input" type="email" required maxLength={200}
            autoComplete="email" value={form.email} onChange={update("email")} disabled={sending}
          />
        </div>

        <div className="form__field">
          <label className="form__label" htmlFor="cf-phone">
            연락처 {isPartner ? <Req /> : <span className="form__opt">(선택)</span>}
          </label>
          <input
            id="cf-phone" className="form__input" type="tel" maxLength={40}
            autoComplete="tel" value={form.phone} onChange={update("phone")} disabled={sending}
          />
        </div>
      </div>

      <div className="form__row">
        <div className="form__field">
          <label className="form__label" htmlFor="cf-type">
            문의 유형 <span className="form__req" aria-hidden="true">*</span>
          </label>
          <select
            id="cf-type" className="form__input form__select" required
            value={form.inquiry_type} onChange={update("inquiry_type")} disabled={sending}
          >
            <option value="">선택해주세요</option>
            {INQUIRY_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>

        <div className="form__field">
          <label className="form__label" htmlFor="cf-stage">
            현재 단계 <span className="form__req" aria-hidden="true">*</span>
          </label>
          <select
            id="cf-stage" className="form__input form__select" required
            value={form.stage} onChange={update("stage")} disabled={sending}
          >
            <option value="">선택해주세요</option>
            {PROJECT_STAGES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
      </div>

      {isPartner && (
        <fieldset className="form__partner" disabled={sending}>
          <legend className="form__partner-title">공동개발 검토 정보</legend>
          <p className="form__hint">{PARTNER.done}</p>
          <div className="form__field">
            <span className="form__label">사업자 형태 <Req /></span>
            <Choice name="biz_type" options={PARTNER.bizTypes} value={form.biz_type} onChange={update("biz_type")} />
          </div>
          <div className="form__field">
            <label className="form__label" htmlFor="cf-seg">현재 하는 일·업종 <Req /></label>
            <select id="cf-seg" className="form__input form__select" value={form.segment} onChange={update("segment")}>
              <option value="">선택해주세요</option>
              {SEGMENTS.map((s) => <option key={s.id} value={s.label}>{s.label}</option>)}
              <option value={SEGMENT_OTHER}>{SEGMENT_OTHER}</option>
            </select>
            {isOther && (
              <input className="form__input form__sub-input" type="text" maxLength={120}
                     placeholder="하시는 일을 적어주세요" aria-label="업종 직접 입력"
                     value={form.industry_other} onChange={update("industry_other")} />
            )}
          </div>
          <div className="form__field">
            <span className="form__label">판매 방법·판로가 있는가 <Req /></span>
            <Choice name="sales_channel" options={PARTNER.salesChannels} value={form.sales_channel} onChange={update("sales_channel")} />
          </div>
          <div className="form__field">
            <span className="form__label">정부지원사업 <Req /></span>
            <Choice name="gov_support" options={PARTNER.govSupport} value={form.gov_support} onChange={update("gov_support")} />
          </div>
          {isFunded && (
            <div className="form__gov">
              <div className="form__field">
                <label className="form__label" htmlFor="cf-gprog">지원사업명 <Req /></label>
                <input id="cf-gprog" className="form__input" type="text" maxLength={200}
                       value={form.gov_program} onChange={update("gov_program")} />
              </div>
              <div className="form__row">
                <div className="form__field">
                  <label className="form__label" htmlFor="cf-gbud">개발 가능 예산 <Req /></label>
                  <input id="cf-gbud" className="form__input" type="text" maxLength={60} placeholder="예: 2,000만원"
                         value={form.gov_budget} onChange={update("gov_budget")} />
                </div>
                <div className="form__field">
                  <label className="form__label" htmlFor="cf-gend">협약 종료일 <Req /></label>
                  <input id="cf-gend" className="form__input" type="date"
                         value={form.gov_end_date} onChange={update("gov_end_date")} />
                </div>
              </div>
            </div>
          )}
        </fieldset>
      )}

      <div className="form__row">
        <div className="form__field">
          <label className="form__label" htmlFor="cf-schedule">
            희망 일정 <span className="form__opt">(선택)</span>
          </label>
          <input
            id="cf-schedule" className="form__input" type="text" maxLength={120}
            placeholder="예: 2027년 상반기 내 시제품"
            value={form.schedule} onChange={update("schedule")} disabled={sending}
          />
        </div>

        <div className="form__field">
          <label className="form__label" htmlFor="cf-budget">
            예산 범위 <span className="form__opt">(선택)</span>
          </label>
          <select
            id="cf-budget" className="form__input form__select"
            value={form.budget} onChange={update("budget")} disabled={sending}
          >
            <option value="">선택 안 함</option>
            {BUDGET_RANGES.map((b) => <option key={b} value={b}>{b}</option>)}
          </select>
        </div>
      </div>

      <div className="form__field">
        <label className="form__label" htmlFor="cf-message">
          내용 <span className="form__req" aria-hidden="true">*</span>
        </label>
        <textarea
          id="cf-message" className="form__input form__textarea" required rows={6} maxLength={5000}
          placeholder={isPartner
            ? "해결하려는 문제(현장에서 어떤 불편이 있는지), 생각하는 제품(모양 · 기능 · 사용 장면), 누가 사거나 쓸 것인지를 적어주세요. 예상 판매가 · 수량을 알면 함께 적어주세요."
            : "만들고 싶은 것과 이미 준비된 것(사양서, 기존 제품, 사진 등)을 함께 적어주시면 검토가 빨라집니다."}
          value={form.message} onChange={update("message")} disabled={sending}
        />
      </div>

      <div className="form__field">
        <label className="form__label" htmlFor="cf-file">
          첨부 파일 <span className="form__opt">(선택)</span>
        </label>
        <input
          ref={fileRef}
          id="cf-file" className="form__file" type="file"
          accept={ATTACHMENT.accept} onChange={onPickFile} disabled={sending}
          aria-describedby="cf-file-hint"
        />
        {file ? (
          <p className="form__file-picked">
            {file.name} <span>{formatSize(file.size)}</span>
            <button type="button" className="form__file-clear" onClick={clearFile} disabled={sending}>
              제거
            </button>
          </p>
        ) : (
          <p id="cf-file-hint" className="form__hint">{ATTACHMENT.hint}</p>
        )}
      </div>

      {/* 봇 트랩 — 사람에게는 보이지 않음 */}
      <div className="form__trap" aria-hidden="true">
        <label htmlFor="cf-website">홈페이지</label>
        <input
          id="cf-website" type="text" tabIndex={-1} autoComplete="off"
          value={form.website} onChange={update("website")}
        />
      </div>

      <div className="form__agree">
        <input
          id="cf-privacy" type="checkbox" checked={agree}
          onChange={(e) => setAgree(e.target.checked)} disabled={sending}
        />
        <label htmlFor="cf-privacy">
          <Link to="/privacy">개인정보 수집·이용</Link>에 동의합니다.
          <span className="form__req" aria-hidden="true"> *</span>
        </label>
      </div>

      {status === "error" && (
        <p className="form__error" role="alert">{error}</p>
      )}

      <button type="submit" className="btn btn--primary btn--block" disabled={sending}>
        {sending
          ? (file && progress > 0 && progress < 100
              ? `첨부 올리는 중… ${progress}%`
              : "보내는 중…")
          : "개발 문의 보내기"}
        {!sending && <Icon.ArrowRight width="16" height="16" aria-hidden="true" focusable="false" />}
      </button>

      {sending && file && (
        <div className="form__progress" aria-hidden="true">
          <div className="form__progress-bar" style={{ width: `${progress}%` }} />
        </div>
      )}

      <p className="form__note">
        보내주신 내용은 문의 회신 목적으로만 사용합니다. 메일 앱이 열리지 않아도 바로 접수됩니다.
        <br />
        직접 연락을 원하시면 <a href={`mailto:${COMPANY.email}`}>{COMPANY.email}</a> ·{" "}
        <a href={`tel:${COMPANY.phone.replace(/-/g, "")}`}>{COMPANY.phone}</a>
      </p>
    </form>
  );
}
